import { Search, Users } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import {
    fetchAllMemberGroups,
    searchMemberGroups,
} from '../api/memberGroups';
import { MemberCard } from '../components/MemberCard';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';
import { getMembersByIds, normalizeMemberSearchText } from '../data/members';
import { OCAKBASI_GROUPS } from '../data/ocakbasiGroups';

const GROUP_SEARCH_DEBOUNCE_MS = 350;
const GROUP_SIDEBAR_SKELETON_COUNT = 7;
const MEMBER_SKELETON_ROWS = 4;

/**
 * @param {{ statusLabel: string }} props
 */
function OcakbasiGruplariPageSkeleton({ statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <div className="ocakbasi-groups-shell">
                    <aside className="ocakbasi-groups-aside" aria-hidden>
                        <p className="ocakbasi-groups-aside-label">
                            <Skeleton width={132} height={13} borderRadius={4} />
                        </p>
                        <div className="ocakbasi-groups-search-wrap">
                            <Skeleton
                                height={38}
                                borderRadius={8}
                                className="d-block"
                            />
                        </div>
                        <ul className="ocakbasi-groups-list list-unstyled mb-0">
                            {Array.from(
                                { length: GROUP_SIDEBAR_SKELETON_COUNT },
                                (_, i) => (
                                    <li key={`ocakbasi-aside-sk-${i}`}>
                                        <div
                                            className="ocakbasi-groups-item ocakbasi-groups-item--skeleton"
                                            aria-hidden
                                        >
                                            <span className="ocakbasi-groups-item-name">
                                                <Skeleton
                                                    height={14}
                                                    width="82%"
                                                    borderRadius={4}
                                                />
                                            </span>
                                            <span className="ocakbasi-groups-item-count">
                                                <Skeleton
                                                    width={24}
                                                    height={20}
                                                    borderRadius={999}
                                                />
                                            </span>
                                        </div>
                                    </li>
                                ),
                            )}
                        </ul>
                    </aside>

                    <section
                        className="ocakbasi-groups-panel members-page members-page--group-list"
                        aria-hidden
                    >
                        <header className="ocakbasi-groups-panel-head">
                            <div className="ocakbasi-groups-panel-title-row">
                                <Skeleton
                                    width={28}
                                    height={28}
                                    borderRadius={8}
                                    className="flex-shrink-0 ocakbasi-groups-panel-icon-skeleton"
                                />
                                <div className="min-w-0 flex-grow-1">
                                    <Skeleton
                                        height={24}
                                        width="68%"
                                        className="mb-2"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={14}
                                        width="42%"
                                        borderRadius={4}
                                    />
                                </div>
                            </div>
                        </header>
                        <ul className="members-list members-list--skeleton list-unstyled mb-0">
                            {Array.from(
                                { length: MEMBER_SKELETON_ROWS },
                                (_, i) => (
                                    <li key={`ocakbasi-member-sk-${i}`}>
                                        <article
                                            className="member-card member-card--skeleton member-card--group-list"
                                            aria-hidden
                                        >
                                            <div className="member-card-photos">
                                                <Skeleton
                                                    width={68}
                                                    height={68}
                                                    borderRadius={8}
                                                />
                                                <Skeleton
                                                    width={68}
                                                    height={68}
                                                    borderRadius={8}
                                                />
                                            </div>
                                            <div className="member-card-names">
                                                <Skeleton
                                                    height={20}
                                                    width="88%"
                                                    className="mb-2"
                                                    borderRadius={6}
                                                />
                                                <Skeleton
                                                    height={15}
                                                    width="58%"
                                                    borderRadius={6}
                                                />
                                            </div>
                                        </article>
                                    </li>
                                ),
                            )}
                        </ul>
                    </section>
                </div>
            </SkeletonTheme>
        </div>
    );
}

/**
 * @typedef {import('../api/memberGroups').MemberGroupUi} MemberGroupUi
 */

function buildStaticUiGroups() {
    return OCAKBASI_GROUPS.map((g) => ({
        id: g.id,
        name: g.name,
        description: '',
        leaderId: null,
        members: getMembersByIds(g.memberIds),
    }));
}

/**
 * Ocakbaşı grupları — tek ekranda grup seçimi ve üye listesi.
 * URL: /member-area/fellowship-groups
 *
 * Tam liste: `GET .../members/api/Group`. Grup araması:
 * `GET .../members/api/Group/search?keyword=&pageNumber=&pageSize=` (canlı oturum).
 * Simülasyon / ilk yükleme hatası: örnek `OCAKBASI_GROUPS` (istemci tarafı filtre).
 */
export function OcakbasiGruplariPage() {
    const { t } = useTranslation();
    const { authToken, isSimulatedAuth } = useAuth();
    const staticUiGroups = useMemo(() => buildStaticUiGroups(), []);
    const useLiveApi = Boolean(authToken && !isSimulatedAuth);

    const [apiGroups, setApiGroups] = useState(
        /** @type {MemberGroupUi[] | null} */ (null),
    );
    const [loadError, setLoadError] = useState(
        /** @type {string | null} */ (null),
    );
    const [loading, setLoading] = useState(false);

    const [groupSearch, setGroupSearch] = useState('');
    const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('');
    const [searchResults, setSearchResults] = useState(
        /** @type {MemberGroupUi[] | null} */ (null),
    );
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(
        /** @type {string | null} */ (null),
    );

    useEffect(() => {
        const debounceTimer = window.setTimeout(
            () => setDebouncedGroupSearch(groupSearch.trim()),
            GROUP_SEARCH_DEBOUNCE_MS,
        );
        return () => window.clearTimeout(debounceTimer);
    }, [groupSearch]);

    useEffect(() => {
        if (!useLiveApi) {
            setApiGroups(null);
            setLoadError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);

        (async () => {
            try {
                const list = await fetchAllMemberGroups(authToken);
                if (cancelled) return;
                setApiGroups(list);
            } catch (err) {
                if (cancelled) return;
                setApiGroups(null);
                setLoadError(
                    err instanceof Error
                        ? err.message
                        : t('ocakbasiGroupsPage.genericGroupsLoadError'),
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [useLiveApi, authToken, t]);

    useEffect(() => {
        if (!useLiveApi || !authToken) {
            setSearchResults(null);
            setSearchLoading(false);
            setSearchError(null);
            return undefined;
        }

        const keyword = debouncedGroupSearch.trim();
        if (!keyword) {
            setSearchResults(null);
            setSearchLoading(false);
            setSearchError(null);
            return undefined;
        }

        const controller = new AbortController();
        setSearchLoading(true);
        setSearchError(null);

        (async () => {
            try {
                const results = await searchMemberGroups(authToken, keyword, {
                    signal: controller.signal,
                });
                if (controller.signal.aborted) return;
                setSearchResults(results);
                setSearchError(null);
            } catch (err) {
                if (controller.signal.aborted) return;
                setSearchResults(null);
                setSearchError(
                    err instanceof Error
                        ? err.message
                        : t('ocakbasiGroupsPage.genericGroupSearchError'),
                );
            } finally {
                if (!controller.signal.aborted) {
                    setSearchLoading(false);
                }
            }
        })();

        return () => {
            controller.abort();
        };
    }, [useLiveApi, authToken, debouncedGroupSearch, t]);

    const offlineGroups = staticUiGroups;

    const groups = useLiveApi
        ? loadError
            ? offlineGroups
            : (apiGroups ?? null)
        : offlineGroups;

    const searchPending =
        Boolean(groupSearch.trim()) &&
        (groupSearch.trim() !== debouncedGroupSearch || searchLoading);

    const sidebarGroups = useMemo(() => {
        if (!groups) return [];
        const q = debouncedGroupSearch.trim();
        if (!q) return groups;

        if (useLiveApi) {
            if (searchError) {
                const normalized = normalizeMemberSearchText(q);
                return groups.filter((g) =>
                    normalizeMemberSearchText(g.name).includes(normalized),
                );
            }
            if (searchLoading) return searchResults ?? [];
            return searchResults ?? [];
        }

        const normalized = normalizeMemberSearchText(q);
        return groups.filter((g) =>
            normalizeMemberSearchText(g.name).includes(normalized),
        );
    }, [
        groups,
        debouncedGroupSearch,
        useLiveApi,
        searchError,
        searchLoading,
        searchResults,
    ]);

    const [selectedId, setSelectedId] = useState('');
    const panelId = useId();

    useEffect(() => {
        if (sidebarGroups.length > 0) {
            if (!sidebarGroups.some((g) => g.id === selectedId)) {
                setSelectedId(sidebarGroups[0].id);
            }
            return;
        }
        if (
            groups &&
            groups.length > 0 &&
            (!selectedId || !groups.some((g) => g.id === selectedId))
        ) {
            setSelectedId(groups[0].id);
        }
    }, [sidebarGroups, groups, selectedId]);

    const selected = useMemo(() => {
        if (sidebarGroups.length === 0) return null;
        const inFilter = sidebarGroups.find((g) => g.id === selectedId);
        return inFilter ?? sidebarGroups[0];
    }, [selectedId, sidebarGroups]);

    const members = selected?.members ?? [];

    const selectGroup = useCallback((id) => {
        setSelectedId(id);
    }, []);

    if (useLiveApi && loading && !loadError) {
        return (
            <>
                <PageSeo
                    title={`${t('ocakbasiGroupsPage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.ocakbasiGroups')}
                    noindex
                />
                <SubpageLayout
                    title={t('ocakbasiGroupsPage.title')}
                    subtitle={t('ocakbasiGroupsPage.subtitle')}
                    contentClassName="ocakbasi-groups-page"
                >
                    <OcakbasiGruplariPageSkeleton
                        statusLabel={t('ocakbasiGroupsPage.loadingGroups')}
                    />
                </SubpageLayout>
            </>
        );
    }

    if (!groups || groups.length === 0) {
        return (
            <>
                <PageSeo
                    title={`${t('ocakbasiGroupsPage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.ocakbasiGroups')}
                    noindex
                />
                <SubpageLayout
                    title={t('ocakbasiGroupsPage.title')}
                    subtitle={t('ocakbasiGroupsPage.subtitleNoGroups')}
                    contentClassName="ocakbasi-groups-page"
                >
                    <p className="ocakbasi-groups-empty mb-0 text-secondary">
                        {useLiveApi && !loadError
                            ? t('ocakbasiGroupsPage.emptyServerNoGroups')
                            : t('ocakbasiGroupsPage.emptyStaticNoGroups')}
                    </p>
                </SubpageLayout>
            </>
        );
    }

    const showSearchNotice =
        Boolean(debouncedGroupSearch) && searchPending;

    return (
        <>
            <PageSeo
                title={`${t('ocakbasiGroupsPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.ocakbasiGroups')}
                noindex
            />
            <SubpageLayout
                title={t('ocakbasiGroupsPage.title')}
                subtitle={t('ocakbasiGroupsPage.subtitle')}
                contentClassName="ocakbasi-groups-page"
            >
            {loadError ? (
                <p
                    className="text-center small text-secondary mb-3"
                    role="status"
                >
                    {t('ocakbasiGroupsPage.loadErrorNotice', {
                        error: loadError,
                    })}
                </p>
            ) : null}
            {searchError && debouncedGroupSearch.trim() ? (
                <p
                    className="text-center small text-secondary mb-3"
                    role="status"
                >
                    {t('ocakbasiGroupsPage.searchApiFallbackNotice', {
                        error: searchError,
                    })}
                </p>
            ) : null}
            <div className="ocakbasi-groups-shell">
                <aside
                    className="ocakbasi-groups-aside"
                    aria-label={t('ocakbasiGroupsPage.asideAriaLabel')}
                >
                    <p className="ocakbasi-groups-aside-label">
                        {t('ocakbasiGroupsPage.asideHeading')}
                    </p>
                    <div className="ocakbasi-groups-search-wrap">
                        <Search
                            className="ocakbasi-groups-search-icon"
                            size={17}
                            strokeWidth={1.85}
                            aria-hidden
                        />
                        <input
                            type="search"
                            className="form-control ocakbasi-groups-search-input"
                            lang="tr"
                            placeholder={
                                useLiveApi
                                    ? t('ocakbasiGroupsPage.placeholderLive')
                                    : t('ocakbasiGroupsPage.placeholderStatic')
                            }
                            value={groupSearch}
                            onChange={(e) => setGroupSearch(e.target.value)}
                            aria-label={t(
                                'ocakbasiGroupsPage.searchInputAriaLabel',
                            )}
                        />
                    </div>
                    {showSearchNotice ? (
                        <p
                            className="ocakbasi-groups-search-status small text-secondary mb-2 mb-md-0 px-1"
                            role="status"
                            aria-live="polite"
                        >
                            {t('ocakbasiGroupsPage.searchingShort')}
                        </p>
                    ) : null}
                    <ul className="ocakbasi-groups-list list-unstyled mb-0">
                            {sidebarGroups.map((g, idx) => {
                                const isOn = g.id === selected?.id;
                                return (
                                    <li key={`${g.id}__${idx}`}>
                                        <button
                                            type="button"
                                            className={`ocakbasi-groups-item${isOn ? ' is-active' : ''}`}
                                            aria-pressed={isOn}
                                            aria-controls={panelId}
                                            onClick={() => selectGroup(g.id)}
                                        >
                                            <span className="ocakbasi-groups-item-name">
                                                {g.name}
                                            </span>
                                            <span className="ocakbasi-groups-item-count">
                                                {g.members.length}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                    </ul>
                </aside>

                <section
                    id={panelId}
                    className="ocakbasi-groups-panel members-page members-page--group-list"
                    aria-live="polite"
                    aria-label={
                        sidebarGroups.length === 0
                            ? t('ocakbasiGroupsPage.panelAriaSearchResults')
                            : t('ocakbasiGroupsPage.panelAriaGroupMembers', {
                                  name: selected?.name ?? '',
                              })
                    }
                >
                    {sidebarGroups.length === 0 ? (
                        <p className="ocakbasi-groups-empty mb-0">
                            {t('ocakbasiGroupsPage.noGroupsMatchSearch')}
                        </p>
                    ) : (
                        <>
                            <header className="ocakbasi-groups-panel-head">
                                <div className="ocakbasi-groups-panel-title-row">
                                    <Users
                                        className="ocakbasi-groups-panel-icon"
                                        size={22}
                                        strokeWidth={1.85}
                                        aria-hidden
                                    />
                                    <div>
                                        <h2 className="ocakbasi-groups-panel-title">
                                            {selected?.name}
                                        </h2>
                                        {selected?.description &&
                                        selected.description !== selected.name ? (
                                            <p className="ocakbasi-groups-panel-desc text-secondary small mb-1">
                                                {selected.description}
                                            </p>
                                        ) : null}
                                        <p className="ocakbasi-groups-panel-meta mb-0">
                                            <strong>{members.length}</strong>{' '}
                                            {t('ocakbasiGroupsPage.membersLabel')}
                                        </p>
                                    </div>
                                </div>
                            </header>

                            {members.length === 0 ? (
                                <p className="ocakbasi-groups-empty mb-0">
                                    {t('ocakbasiGroupsPage.emptyGroupMembers')}
                                </p>
                            ) : (
                                <ul className="members-list list-unstyled mb-0">
                                    {members.map((member) => (
                                        <li key={member.id}>
                                            <MemberCard
                                                member={member}
                                                variant="group"
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </section>
            </div>
        </SubpageLayout>
        </>
    );
}
